import { useEffect, useState } from 'react';
import {
  resolveSourceGate,
  type ConnectableSource,
  type SourceGateState,
} from '../domain/sourceConnection';
import {
  fetchSourceConnectionStatus,
  type SourceConnectionProbe,
} from '../services/sourceConnection';

export interface SourceGateInput {
  error: string | null;
  isLoading: boolean;
  totalCount: number;
}

export interface SourceGate {
  state: SourceGateState;
  /** Provider detail when `state === 'probe_error'`. */
  probeMessage: string | null;
}

/**
 * Decides whether a feed should render its normal chrome or the connection
 * panel. The probe only runs while the list is empty, idle and error-free;
 * a not-configured load error short-circuits without probing.
 */
export function useSourceConnectionGate(
  source: ConnectableSource,
  input: SourceGateInput,
): SourceGate {
  const shouldProbe = !input.isLoading && !input.error && input.totalCount === 0;
  const [probe, setProbe] = useState<SourceConnectionProbe | null>(null);

  useEffect(() => {
    if (!shouldProbe) {
      setProbe(null);
      return;
    }
    let active = true;
    void fetchSourceConnectionStatus(source).then((result) => {
      if (active) setProbe(result);
    });
    return () => {
      active = false;
    };
  }, [source, shouldProbe]);

  return {
    state: resolveSourceGate({ ...input, probe: shouldProbe ? probe : null }),
    probeMessage: probe?.message ?? null,
  };
}
